import { isDefined } from "@clipboard-health/util-ts";
import throttle from "lodash/throttle";
import { type RefObject, useEffect, useState } from "react";

import { type PlacementDetailsSectionId } from "../../../placementDetailsSections";

// Account for the sticky chip row when deciding which section is currently active.
const ACTIVE_SECTION_TOP_OFFSET_PX = 60;
const BOTTOM_SCROLL_THRESHOLD_PX = 1;
const THROTTLE_MS = 150;

export function useActiveSectionChip(
  scrollContainerRef: RefObject<HTMLDivElement>,
  sectionIds: readonly PlacementDetailsSectionId[]
) {
  const [activeSectionId, setActiveSectionId] = useState<PlacementDetailsSectionId | undefined>(
    sectionIds[0]
  );

  useEffect(() => {
    const [fallbackSectionId] = sectionIds;
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer || !isDefined(fallbackSectionId)) {
      setActiveSectionId((previousActiveSectionId) => {
        if (previousActiveSectionId === fallbackSectionId) {
          return previousActiveSectionId;
        }

        return fallbackSectionId;
      });

      return undefined;
    }

    const syncActiveSection = () => {
      const nextActiveSectionId = getActiveSectionId({
        scrollContainer,
        sectionIds,
        fallbackSectionId,
      });

      setActiveSectionId((previousActiveSectionId) => {
        if (previousActiveSectionId === nextActiveSectionId) {
          return previousActiveSectionId;
        }

        return nextActiveSectionId;
      });
    };

    const throttledSyncActiveSection = throttle(syncActiveSection, THROTTLE_MS);

    scrollContainer.addEventListener("scroll", throttledSyncActiveSection, { passive: true });
    syncActiveSection();

    return () => {
      scrollContainer.removeEventListener("scroll", throttledSyncActiveSection);
      throttledSyncActiveSection.cancel();
    };
  }, [scrollContainerRef, sectionIds]);

  return activeSectionId;
}

interface GetActiveSectionIdParams {
  scrollContainer: HTMLDivElement;
  sectionIds: readonly PlacementDetailsSectionId[];
  fallbackSectionId: PlacementDetailsSectionId;
}

function getActiveSectionId(params: GetActiveSectionIdParams) {
  const { scrollContainer, sectionIds, fallbackSectionId } = params;

  if (isScrollContainerAtBottom(scrollContainer)) {
    return sectionIds[sectionIds.length - 1] ?? fallbackSectionId;
  }

  const scrollContainerRect = scrollContainer.getBoundingClientRect();
  let activeSectionId = fallbackSectionId;

  for (const sectionId of sectionIds) {
    const sectionTopOffset = getSectionTopOffset({
      sectionId,
      scrollContainerTop: scrollContainerRect.top,
    });

    if (sectionTopOffset === undefined) {
      continue;
    }

    if (sectionTopOffset <= ACTIVE_SECTION_TOP_OFFSET_PX) {
      activeSectionId = sectionId;
    }
  }

  return activeSectionId;
}

function isScrollContainerAtBottom(scrollContainer: HTMLDivElement) {
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

  if (maxScrollTop <= BOTTOM_SCROLL_THRESHOLD_PX) {
    return false;
  }

  return maxScrollTop - scrollContainer.scrollTop <= BOTTOM_SCROLL_THRESHOLD_PX;
}

interface GetSectionTopOffsetParams {
  sectionId: PlacementDetailsSectionId;
  scrollContainerTop: number;
}

function getSectionTopOffset(params: GetSectionTopOffsetParams) {
  const { sectionId, scrollContainerTop } = params;
  const sectionElement = document.getElementById(sectionId);

  if (!sectionElement) {
    return undefined;
  }

  const sectionRect = sectionElement.getBoundingClientRect();
  return sectionRect.top - scrollContainerTop;
}
